// Departure + membership bookkeeping for `EntityStateStore` (BUG-1766): an entity that
// LEFT the topic (a lot moved or removed out of the auction) must leave the fold, stay
// out of it against older late deltas, and be found by a reconcile even when its
// departure frame was lost. Pure helpers and one small ledger; the store calls them at
// its fold, reconcile and reseed seams.

import type { RealtimeEventName } from '../../types/realtime/events';

/** Entities that departed the topic, keyed to the number they departed at. */
export class DepartureLedger {
    private readonly departedAt = new Map<string, number>();

    /**
     * Record that `key` left the topic at `cursor`. Keeps the higher of two marks, so an
     * out-of-order pair of departures resolves to the later one.
     *
     * @param key - the entity's fold key.
     * @param cursor - its departure's position in the topic's series.
     */
    mark(key: string, cursor: number): void {
        this.departedAt.set(key, Math.max(cursor, this.departedAt.get(key) ?? 0));
    }

    /**
     * Whether a delta at `cursor` for `key` predates that entity's departure — a stale
     * frame that must not re-add it. A NEWER delta (the entity came back) clears the mark.
     *
     * @param key - the delta's fold key.
     * @param cursor - the delta's position.
     * @returns `true` when the delta must be dropped.
     */
    isBeforeDeparture(key: string, cursor: number): boolean {
        const departed = this.departedAt.get(key);
        if (departed === undefined) return false;
        if (cursor <= departed) return true;
        this.departedAt.delete(key);
        return false;
    }

    /**
     * Forget `key`'s mark — the entity is authoritatively present again.
     *
     * @param key - the entity's fold key.
     */
    clear(key: string): void {
        this.departedAt.delete(key);
    }
}

/**
 * Drop each departed entity from the fold and mark it in the ledger. A departure at or
 * below the entity's held cursor is stale (the entity came back after it) and is ignored.
 *
 * @param opts.entries - the departure frame's `(key, cursor)` pairs.
 * @param opts.working - the store's current fold (not mutated).
 * @param opts.cursorOf - the binding's ordering key.
 * @param opts.ledger - the store's departure ledger.
 * @returns A new fold without the departed entities, or `null` when nothing changed.
 * @sideEffect Marks each non-stale departure in `ledger`.
 */
export function removeDeparted<TDelta>(opts: {
    entries: ReadonlyArray<{ key: string; cursor: number }>;
    working: ReadonlyMap<string, TDelta>;
    cursorOf: (delta: TDelta) => number;
    ledger: DepartureLedger;
}): Map<string, TDelta> | null {
    let next: Map<string, TDelta> | null = null;
    for (const { key, cursor } of opts.entries) {
        const existing = (next ?? opts.working).get(key);
        if (existing !== undefined && cursor <= opts.cursorOf(existing)) continue;
        opts.ledger.mark(key, cursor);
        if (existing === undefined) continue;
        next ??= new Map(opts.working);
        next.delete(key);
    }
    return next;
}

/**
 * The membership check a binding opts into: the scope's live-entity count from a
 * catch-up, and which folded entities count as live.
 */
export interface MembershipConfig<TDelta, TResult> {
    countOf: (result: TResult) => number;
    /** False for a tombstone the fold keeps (a deleted lot, BL-1764). */
    isLive: (delta: TDelta) => boolean;
}

/**
 * Whether the fold holds more live entities than the catch-up counted — i.e. one whose
 * departure it missed. The fold's live set is a superset of the server's (a reconcile
 * only merges), so MORE is the only direction a missed departure can show.
 *
 * @param opts.entities - the store's current fold.
 * @param opts.result - the catch-up just ingested.
 * @param opts.membership - the binding's membership config.
 * @returns `true` when the store should reload and replace its fold.
 */
export function holdsDepartedEntities<TDelta, TResult>(opts: {
    entities: ReadonlyMap<string, TDelta>;
    result: TResult;
    membership: MembershipConfig<TDelta, TResult>;
}): boolean {
    let live = 0;
    for (const delta of opts.entities.values()) if (opts.membership.isLive(delta)) live += 1;
    return live > opts.membership.countOf(opts.result);
}

/**
 * Merge a cold seed over the fold it replaces: the seed is the scope's truth AS OF
 * `position`, so a held LIVE entity it omits at or below that position is a ghost and
 * goes. Everything else the fold knows that the seed cannot is kept:
 *
 * - an entity whose held cursor is ABOVE `position` — a delta that landed while the
 *   seed fetch was in flight, newer than the seed;
 * - a held non-live entity (a tombstone) the seed omits — it must stay, or an older
 *   late delta could resurrect it.
 *
 * Pure — the store applies the result.
 *
 * @param opts.seed - the cold seed's entities (already scope-filtered).
 * @param opts.held - the store's fold at the moment the seed arrived.
 * @param opts.position - the seed's position in the series.
 * @returns The merged fold and the keys dropped as ghosts.
 */
export function mergeReseed<TDelta>(opts: {
    seed: ReadonlyArray<TDelta>;
    held: ReadonlyMap<string, TDelta>;
    position: number;
    keyOf: (delta: TDelta) => string;
    cursorOf: (delta: TDelta) => number;
    isLive: (delta: TDelta) => boolean;
}): { next: Map<string, TDelta>; dropped: string[] } {
    const next = new Map<string, TDelta>();
    for (const delta of opts.seed) {
        const key = opts.keyOf(delta);
        const existing = next.get(key);
        if (existing === undefined || opts.cursorOf(delta) > opts.cursorOf(existing)) next.set(key, delta);
    }
    const dropped: string[] = [];
    for (const [key, delta] of opts.held) {
        const seeded = next.get(key);
        if (seeded !== undefined) {
            if (opts.cursorOf(delta) > opts.cursorOf(seeded)) next.set(key, delta);
            continue;
        }
        if (opts.cursorOf(delta) > opts.position || !opts.isLive(delta)) next.set(key, delta);
        else dropped.push(key);
    }
    return { next, dropped };
}

/**
 * A second event on a store's topic naming entities that LEFT it (BUG-1766 — a lot
 * moved or removed out of the auction). Each entry's `cursor` is a number in the
 * topic's one series, so it counts toward the store's position and is gap-checked
 * exactly like a delta. `removes` is false for a scoped store: it must still count the
 * position (see `EntityStateStoreConfig.retains`), but whether a scoped reader should
 * lose its one entity is not the engine's call.
 */
export interface DeparturesConfig {
    event: RealtimeEventName;
    entriesOf: (payload: unknown) => ReadonlyArray<{ key: string; cursor: number }>;
    removes: boolean;
}

/** What a `DepartureTracker` needs from the store that owns it. */
export interface DepartureHost<TDelta, TResult> {
    /** The store's current (possibly unpublished) fold. */
    working: () => ReadonlyMap<string, TDelta>;
    /** Stage `next` as the fold and schedule a render. */
    commit: (next: Map<string, TDelta>) => void;
    /** Advance the store's position to at least `position`. */
    advance: (position: number) => void;
    /** Gap-check a frame's positions, as for a delta frame. */
    checkGap: (positions: readonly number[]) => void;
    /** A cold-seed catch-up (`since = 0`). */
    fetchSeed: () => Promise<TResult>;
    /** The catch-up result's position in the series. */
    positionOf: (result: TResult) => number;
    itemsOf: (result: TResult) => readonly TDelta[];
    keyOf: (delta: TDelta) => string;
    cursorOf: (delta: TDelta) => number;
    retains?: (delta: TDelta) => boolean;
}

/**
 * Everything `EntityStateStore` does about entities leaving its topic (BUG-1766): apply
 * departure frames, keep stale deltas from re-adding a departed entity, and — when a
 * reconcile shows a departure was missed — reload and merge the fold.
 */
export class DepartureTracker<TDelta, TResult> {
    private readonly ledger = new DepartureLedger();

    constructor(
        private readonly host: DepartureHost<TDelta, TResult>,
        private readonly membership: MembershipConfig<TDelta, TResult> | undefined,
    ) {}

    /**
     * Apply one departure frame: count and gap-check its numbers, then (unscoped
     * stores) drop each departed entity and mark it.
     *
     * @param entries - the frame's `(key, cursor)` pairs.
     * @param removes - whether this store drops departed entities.
     */
    onFrame(entries: ReadonlyArray<{ key: string; cursor: number }>, removes: boolean): void {
        this.host.checkGap(entries.map((e) => e.cursor));
        for (const { cursor } of entries) this.host.advance(cursor);
        if (!removes) return;
        const next = removeDeparted({
            entries,
            working: this.host.working(),
            cursorOf: this.host.cursorOf,
            ledger: this.ledger,
        });
        if (next !== null) this.host.commit(next);
    }

    /**
     * Whether a delta predates its entity's departure (see {@link DepartureLedger}).
     *
     * @param key - the delta's fold key.
     * @param cursor - the delta's position.
     * @returns `true` when the delta must be dropped.
     */
    isBeforeDeparture(key: string, cursor: number): boolean {
        return this.ledger.isBeforeDeparture(key, cursor);
    }

    /**
     * After a reconcile: when the fold holds more live entities than the catch-up
     * counted, reload the scope and merge it over the fold.
     *
     * The fold is re-read AFTER the fetch, so a delta that landed while it was in
     * flight (newer than the seed) survives, as does a held tombstone (see
     * `mergeReseed`). Each dropped ghost is marked departed at the seed's position.
     *
     * @param result - the catch-up just ingested.
     * @sideEffect At most one cold-seed HTTP catch-up — only on a mismatch, which a
     *   healthy socket never produces.
     */
    async reconcileMembership(result: TResult): Promise<void> {
        const membership = this.membership;
        if (!membership || !holdsDepartedEntities({ entities: this.host.working(), result, membership })) return;
        const seed = await this.host.fetchSeed();
        const position = this.host.positionOf(seed);
        const retains = this.host.retains;
        const { next, dropped } = mergeReseed({
            seed: this.host.itemsOf(seed).filter((delta) => !retains || retains(delta)),
            held: this.host.working(),
            position,
            keyOf: this.host.keyOf,
            cursorOf: this.host.cursorOf,
            isLive: membership.isLive,
        });
        for (const key of next.keys()) this.ledger.clear(key);
        for (const key of dropped) this.ledger.mark(key, position);
        this.host.advance(position);
        this.host.commit(next);
    }
}
